<script lang="ts">
	import { PUBLIC_BASE_URL } from '$env/static/public';
  import { browser } from "$app/environment";
  import type { PageData } from "./$types";
  import { customPageOptions } from '$lib/utils/layout-store';
  import { GtmStore } from '$lib/utils/gtm-store';
  import { StructureUtils } from '$lib/utils/structured-utils';
  // Components
  import { LinkTo } from '$lib/components/atoms/Link';
  import { Head } from '$lib/components/atoms/Head'
  import { Breadcrumb, BreadcrumbItem } from '$lib/components/molecules/Breadcrumb';
  import { FeatureBoxes, Gallery } from '$lib/components/organisms/Layout';
  import { FeatureBoxCard } from '$lib/components/organisms/Card';
  import { HeroStandard } from '$lib/components/organisms/Hero';
  import { Section, SectionImage, SectionTight } from '$lib/components/organisms/Section';
	import type { IStaticPage } from "$lib/utils/contentful/types";
  
  export let data: PageData;

  customPageOptions({ bodyStyle: 'gray' });

  /******************** GTM EVENT PUSH *************************/
  GtmStore.pushEvent({
    pageCategory: 'About',
  });
  /*************************************************************/

  const breadcrumbJson = StructureUtils.getBreadcrumbStructure(
    [
      {
        name: 'Home',
        url: PUBLIC_BASE_URL,
      },
      {
        name: 'Chi siamo',
        url: `${PUBLIC_BASE_URL}chi-siamo`,
      },
    ],
    true
  );

  const aggregateRatingJson = StructureUtils.getAggregateStructure(null, null, true);

  let ldScriptTag = '';
  ldScriptTag = StructureUtils.createScriptTag(JSON.stringify([breadcrumbJson, aggregateRatingJson]));

  const metaTagData: IStaticPage | null = data?.metaTagData ? JSON.parse(data.metaTagData) : null;

  const seoDetails = {
    title: metaTagData?.fields?.metaTagSocialTitle|| 'Chi siamo',
    description: metaTagData?.fields?.metaTagSocialDescription || 'Dal 1993 formiamo neolaureati, manager e professionisti per aiutarli a definire e a realizzare il loro percorso professionale con un’offerta innovativa e differenziata per industry e aree tematiche.',
    metaTagSocialTitle: metaTagData?.fields?.metaTagSocialTitle || '',
    metaTagSocialDescription: metaTagData?.fields?.metaTagSocialDescription || '',
    url: '/about',
    image: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
    metaTagSocialImage: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
    ldScriptTag: ldScriptTag,
  };

    if (metaTagData && metaTagData.fields) {
    if (metaTagData.fields.metaTagSocialTitle) {
      seoDetails.title = metaTagData.fields.metaTagSocialTitle;
    }
    if (metaTagData.fields.metaTagSocialDescription) {
      seoDetails.description = metaTagData.fields.metaTagSocialDescription;
    }
    if (metaTagData.fields.metaTagSocialImage) {
      seoDetails.image = metaTagData.fields.metaTagSocialImage.fields.file.url;
    }
  }
</script>

<Head appendCompany {seoDetails} />

<Breadcrumb disableLastElementLink>
  <BreadcrumbItem>
    <a href="/">Home</a>
  </BreadcrumbItem>
  <BreadcrumbItem current>
    <a href="/about">Chi siamo</a>
  </BreadcrumbItem>
</Breadcrumb>

<HeroStandard
  darkOverlay
  data={{
    image: '/assets/placeholders/mobile/hero-about.webp',
    srcset: '/assets/placeholders/desktop/hero-about.webp 1x, /assets/placeholders/high/hero-about.webp 2x',
    imageAlt: 'Laureati',
    subtitle: 'Dal 1994, verso il futuro',
    title: 'La Prima Digital Business School Italiana',
  }}
  taller
/>

<SectionTight compact tighter variant="transparent" class="pt-3 pb-6 md:pt-6">
  <div slot="header">
    <p class="py-6">
      <b>24ORE Business School, fondata nel 1994 da Il Sole 24 Ore, è da oltre 30 anni la Digital Business School leader in Italia. </b><br/>
      Crediamo che la formazione e il self-development permettano di scegliere il proprio futuro, creando valore condiviso. È per questo motivo che, in 24ORE Business School, la presenza di docenti di estrazione manageriale e l’approccio learning by doing si affiancano a contenuti pensati per creare un equilibrio tra crescita personale e professionale e ad un impegno nell’ispirare costantemente gli studenti.
    </p>
    <p>
      Questo approccio unico, da oltre 30 anni, permette di offrire un'ampia gamma di corsi per:
    </p>
    <ul class="pb-6 list-disc">
      <li class="ml-4">
        <strong>neolaureati e giovani professionisti</strong> che desiderano accelerare l’ingresso nel mondo del lavoro, acquisendo competenze pratiche e affrontando sfide complesse sin dai primi passi della loro carriera
      </li>
      <li class="ml-4">
        <strong>professionisti e manager</strong> che intendono dare una svolta alla propria carriera sviluppando nuove competenze e strumenti avanzati per raggiungere obiettivi più ambiziosi e progredire nel loro percorso professionale
      </li>
      <li class="ml-4">
        <strong>imprenditori e startupper</strong> che aspirano a migliorare le capacità di gestione aziendale, ottimizzare i processi interni e guidare con successo le loro aziende verso una crescita sostenibile.
      </li>
    </ul>
    <p class="pb-6">
      L’offerta di 24ORE Business School si rivolge anche alle aziende e ai decisori interni che sono deputati all'acquisto di percorsi di formazione per la popolazione aziendale, quali risorse umane, manager o altri rappresentanti della direzione aziendale che hanno il compito di valutare e selezionare programmi formativi per migliorare le competenze e le prestazioni dei dipendenti.
    </p>
  </div>

  <FeatureBoxes>
    <FeatureBoxCard>
      <span slot="kpi">1.300</span>
      <span slot="text">Aziende Partner</span>
    </FeatureBoxCard>
    <FeatureBoxCard color="brand">
      <span slot="kpi">15.000</span>
      <span slot="text">Studenti all'anno formati</span>
    </FeatureBoxCard>
    <FeatureBoxCard>
      <span slot="kpi">1.200</span>
      <span slot="text">Docenti di estrazione manageriale</span>
    </FeatureBoxCard>
  </FeatureBoxes>
  
</SectionTight>

<SectionImage
  backgroundSrc="/assets/placeholders/high/about-section-bg.webp"
  data={{
    subtitle: 'Partners',
    title: 'Collaboriamo con più di 1.300 aziende partner e sponsor',
    description:
      '24ORE Business School collabora con l’eccellenza per offrire ai suoi studenti un confronto unico con le più grandi realtà del mercato.',
  }}
>
  <svelte:fragment slot="action">
    <LinkTo color="light" href="/partners">Scopri i partners</LinkTo>
  </svelte:fragment>
</SectionImage>


<Section class="bg-white py-6 md:py-14">
  <svelte:fragment slot="header">
    <p class="subtitle mb-2">Dove siamo</p>
    <h2 class="fs-3">
      Uniamo fisico e digitale, con lezioni in presenza o streaming
    </h2>
  </svelte:fragment>
</Section>

<Section class="bg-white py-6 md:py-14">
  <svelte:fragment slot="header">
    <p class="subtitle mb-2">Gallery</p>
    <h2 class="fs-3">Creiamo network con aziende di spicco</h2>
  </svelte:fragment>

  <Gallery>
    <img
      src="/assets/placeholders/mobile/gallery/1-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/1-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/1-MasterHR_Inter.webp 2x"
      alt="Inter 1"
    />
    <img
      src="/assets/placeholders/mobile/gallery/2-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/2-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/2-MasterHR_Inter.webp 2x"
      alt="Inter 2"
    />
    <img
      src="/assets/placeholders/mobile/gallery/3-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/3-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/3-MasterHR_Inter.webp 2x"
      alt="Inter 3"
    />
    <img
      src="/assets/placeholders/mobile/gallery/4-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/4-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/4-MasterHR_Inter.webp 2x"
      alt="Inter 4"
    />
    <img
      src="/assets/placeholders/mobile/gallery/5-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/5-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/5-MasterHR_Inter.webp 2x"
      alt="Inter 5"
    />
    <img
      src="/assets/placeholders/mobile/gallery/6-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/6-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/6-MasterHR_Inter.webp 2x"
      alt="Inter 6"
    />
    <img
      src="/assets/placeholders/mobile/gallery/7-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/7-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/7-MasterHR_Inter.webp 2x"
      alt="Inter 7"
    />
    <img
      src="/assets/placeholders/mobile/gallery/8-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/8-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/8-MasterHR_Inter.webp 2x"
      alt="Inter 8"
    />
    <img
      src="/assets/placeholders/mobile/gallery/11-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/11-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/11-MasterHR_Inter.webp 2x"
      alt="Inter 11"
    />
    <img
      src="/assets/placeholders/mobile/gallery/12-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/12-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/12-MasterHR_Inter.webp 2x"
      alt="Inter 12"
    />
    <img
      src="/assets/placeholders/mobile/gallery/14-MasterHR_Inter.webp"
      srcset="/assets/placeholders/desktop/gallery/14-MasterHR_Inter.webp 1x, /assets/placeholders/high/gallery/14-MasterHR_Inter.webp 2x"
      alt="Inter 14"
    />
  </Gallery>
</Section>
